import type {
  AbstractTemplateInOut,
  SubTemplateValueSetupInOut,
  TemplateCreateOperation,
  TemplateFieldValueInOut,
} from '@festina/configuration-general-types/configuration'
import { notEmpty, type Optional } from '@festina-life/core'

import { isModelMetaType, type MetaModel } from '../../types/metaModel'
import type { SubTemplateFieldValue } from '../../types/templateModel'
import { TemplatePathParts } from '../../uiModels/templateOperations/helpers'
import type { WithOptionalOriginInfo } from '../../uiModels/types'

export const valueAtPath = <T>(
  createOperation: WithOptionalOriginInfo<TemplateCreateOperation>,
  path: string,
) => {
  const groups = path.match(TemplatePathParts)?.groups
  const templateId = groups?.['templateId']
  const remainder = groups?.['intermediatePath']

  if (createOperation.entity?.name !== templateId || !remainder?.length) {
    return null
  }

  return navigateAbstractTemplate<T>(
    createOperation.entity as AbstractTemplateInOut,
    // Splits the remaining expression by '.' without including the dots that are found inside a variant (ie. dots followed
    // by \w and ending in a closing bracket }.
    remainder.split(/\.(?![\p{L}\d_\-]+\})/v).filter(Boolean),
  )
}

const navigateAbstractTemplate = <V>(
  value: AbstractTemplateInOut,
  path: string[],
): Optional<V> => {
  if (!path.length) {
    throw new Error('Navigating to an abstract template it not supported')
  }

  const [property, ...rest] = path
  const propertyGroups = property.match(
    /^(?<property>\w+)(\{(?<subTemplateId>[\p{L}\d_\-.]+)\})?/v,
  )?.groups
  const propertyName = propertyGroups?.['property']
  const subTemplateId = propertyGroups?.['subTemplateId']

  if (!propertyName) {
    throw new Error(`Unable to extract a property name from ${propertyName}`)
  }

  const field = value.properties
    ?.filter(notEmpty)
    .find((property) => property.name === propertyName)?.value

  if (
    subTemplateId &&
    field &&
    typeof field === 'object' &&
    '_type' in field &&
    typeof field._type === 'string' &&
    'SubTemplateValueSetupInOut' === field._type
  ) {
    const subtemplatedField = field as SubTemplateValueSetupInOut
    const variant = (subtemplatedField.variants ?? [])
      .filter(notEmpty)
      .find((variant) => variant.name === subTemplateId)

    if (!variant) {
      return null
    }

    return navigateAbstractTemplate<V>(variant, rest)
  } else if (!subTemplateId && field) {
    return navigateFinalTemplateFieldValueInOut<V>(field, rest)
  } else {
    return null
  }
}

const navigateFinalTemplateFieldValueInOut = <V>(
  value: TemplateFieldValueInOut,
  path: string[],
): Optional<V> => {
  if (!path.length) {
    return value as V
  }

  if (path.length > 1) {
    throw new Error(`Unexpected path for final field value ${path}`)
  }

  const [propertyName] = path

  return (value as unknown as Record<string, Optional<V>>)[propertyName]
}

export const isEqualOrSuperType = ({
  evaluatedTypeName,
  modelMetaTypeName,
  metaModel,
}: {
  evaluatedTypeName: string
  modelMetaTypeName: string
  metaModel: MetaModel
}): boolean => {
  if (evaluatedTypeName === modelMetaTypeName) {
    return true
  }

  const modelMetaType = metaModel.types.get(modelMetaTypeName)

  return isModelMetaType(modelMetaType) && modelMetaType?.superTypeName
    ? isEqualOrSuperType({
        evaluatedTypeName,
        modelMetaTypeName: modelMetaType.superTypeName,
        metaModel,
      })
    : false
}

export const parentFromSubTemplateFieldValue = (
  variant: SubTemplateFieldValue,
) => variant.parent?.value
